import { useEffect, useRef, useState } from "react";
import { useAccount, useDisconnect } from "wagmi";
import { cadenas } from "../config/chains";
import { porIdCadena } from "../config/networks";
import { corto } from "../lib/formato";
import { useT } from "../lib/i18n";

/**
 * El menú de la wallet conectada.
 *
 * ANTES ERA UN BOTÓN QUE DESCONECTABA AL PRIMER CLIC, con la dirección de etiqueta y «Desconectar»
 * escondido en el `title`. Es el sitio donde uno pulsa para copiarse la dirección o para mirar en
 * qué red está, y las dos cosas te echaban de la sesión — sin preguntar, y sin más pista que un
 * tooltip que en móvil no existe.
 *
 * Ahora abre, y la acción destructiva vive abajo, separada y en rojo, que es donde se busca cuando
 * de verdad se quiere.
 */
export function MenuWallet() {
  const t = useT();
  const { address, chainId } = useAccount();
  const { disconnect } = useDisconnect();
  const [abierto, setAbierto] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const caja = useRef<HTMLDivElement>(null);

  // Cerrar al pulsar fuera o con Escape. Un menú que solo se cierra con su propio botón es una
  // trampa en móvil, donde el gesto natural es tocar en cualquier otro sitio.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false);
    };
    const tecla = (e: KeyboardEvent) => e.key === "Escape" && setAbierto(false);
    document.addEventListener("mousedown", fuera);
    document.addEventListener("keydown", tecla);
    return () => {
      document.removeEventListener("mousedown", fuera);
      document.removeEventListener("keydown", tecla);
    };
  }, [abierto]);

  if (!address) return null;

  const red = chainId ? porIdCadena(chainId) : undefined;
  const cadena = cadenas.find((c) => c.id === chainId);
  const explorador = cadena?.blockExplorers?.default?.url;

  const copiar = () => {
    navigator.clipboard
      .writeText(address)
      .then(() => {
        setCopiado(true);
        setTimeout(() => setCopiado(false), 2000);
      })
      .catch(() => { /* sin portapapeles: la dirección está a la vista para seleccionarla */ });
  };

  return (
    <div className="menu-wallet" ref={caja}>
      <button
        className={`btn fantasma ${abierto ? "activo" : ""}`}
        onClick={() => setAbierto((x) => !x)}
        aria-haspopup="menu"
        aria-expanded={abierto}
      >
        <span className="punto" aria-hidden />
        {/* La direccion va en su propio `span` para que el movil pueda esconderla: en una pantalla
            estrecha se queda el punto —que dice que estas conectado— y la flecha, y la direccion
            entera vive dentro del menu, que es donde se copia. Suelta en el boton no habia forma
            de ocultarla sin ocultar tambien el punto y la flecha. */}
        <span className="dir">{corto(address)}</span>
        <span className="flecha" aria-hidden>{abierto ? "▴" : "▾"}</span>
      </button>

      {abierto && (
        <div className="panel-wallet" role="menu">
          <div className="cab">
            <span className="k">{t("Conectada")}</span>
            {/* La dirección ENTERA, no la corta: es lo que hay que comparar con la wallet antes de
                firmar, y una versión recortada no sirve para comprobar nada. */}
            <code className="dir-larga">{address}</code>
            {red && <span className="n">{red.etiqueta}</span>}
          </div>

          <button className="opcion" onClick={copiar}>
            <span className="ic" aria-hidden>{copiado ? "✓" : "⧉"}</span>
            {copiado ? t("Dirección copiada") : t("Copiar dirección")}
          </button>

          {explorador && (
            <a
              className="opcion"
              href={`${explorador}/address/${address}`}
              target="_blank"
              rel="noreferrer"
              onClick={() => setAbierto(false)}
            >
              <span className="ic" aria-hidden>↗</span>
              {t("Ver en el explorador")}
            </a>
          )}

          <div className="raya" />

          <button
            className="opcion peligro"
            onClick={() => {
              setAbierto(false);
              disconnect();
            }}
          >
            <span className="ic" aria-hidden>⏻</span>
            {t("Desconectar")}
          </button>
        </div>
      )}
    </div>
  );
}
